import React, { useState, useEffect } from 'react';
import moment from 'moment';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { cloneDeep } from 'lodash';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '@material-ui/core/Button';
import QuoteOption from './QuoteOption';
import { stringIsInt } from '../../../helper';

const useStyles = makeStyles(() => ({
  section: {
    padding: '10px',
    marginBottom: '20px',
    backgroundColor: 'lightgray',
  },
  sectionNameInput: {
    fontSize: 30,
  },
  headerContainerRight: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  optionsContainer: {
    marginTop: '20px',
    display: 'flex',
    padding: '10px 0px',
    overflow: 'auto',
    whiteSpace: 'nowrap',
  },
  addOptionButton: {
    display: 'flex',
    padding: '20px',
    marginLeft: '20px',
    backgroundColor: 'gray',
    opacity: 0.5,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  formControl: {
    minWidth: 120,
  },
}));

const QuoteSection = ({
  sectionIndex,
  section,
  updateSection,
  deleteSection,
  isEditable,
  quoteTemplates = [],
}) => {
  const classes = useStyles();
  const [isDragging, setIsDragging] = useState(false);
  const [deletedOption, setDeletedOption] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);

  useEffect(() => {
    // If this is a newly created section and there's only one template, just select it
    if (!section.id && quoteTemplates && quoteTemplates.length === 1) {
      const onlyTemplate = quoteTemplates[0];
      onSelectTemplate(onlyTemplate.id);
    }
  }, []);

  const updateOption = (index, key, value) => {
    const sectionCopy = cloneDeep(section);
    const newOptions = sectionCopy.options;
    newOptions[index][key] = value;
    updateSection({ options: newOptions });
  };

  const deleteOption = (index) => {
    const sectionCopy = cloneDeep(section);
    const newOptions = sectionCopy.options;
    setDeletedOption(newOptions.splice(index, 1)[0]);
    updateSection({ options: newOptions });
    setUndoVisible(true);
  };

  const onClickUndoDelete = () => {
    const sectionCopy = cloneDeep(section);
    const newOptions = sectionCopy.options;
    newOptions.splice(deletedOption.display_order, 0, deletedOption);
    updateSection({ options: newOptions });
    setUndoVisible(false);
  };

  const makeEmptyOption = (optionIndex, template = section.template) => ({
    answers: template.questions.map((question) => ({
      answer: null,
      question,
    })),
    display_order: optionIndex,
    key: `new-option-${moment().valueOf()}`,
    label: null,
    name: `Option ${optionIndex + 1}`,
    note: null,
    quote_option_id: null,
    quote_section_id: null,
    services: [],
    isNew: true, // This is only used by the frontend
  });

  const onSelectTemplate = (templateID) => {
    const template = quoteTemplates.filter((t) => t.id === templateID)[0];
    updateSection({
      quote_template_id: templateID,
      template,
      options: [makeEmptyOption(0, template)],
    });
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setUndoVisible(false);
  };

  const onDragOptionStart = () => {
    setIsDragging(true);
  };

  // If the option name is in the format "Option 3"
  const optionNameIsDefault = (name) => {
    const nameParts = name.split('Option ');
    return nameParts.length === 2 && stringIsInt(nameParts[1]);
  };

  const onDragOptionEnd = (result) => {
    // If the option is dropped outside the zone
    if (!result.destination) return;

    const reorderedList = Array.from(section.options);
    const [removed] = reorderedList.splice(result.source.index, 1);
    reorderedList.splice(result.destination.index, 0, removed);

    const newOptions = reorderedList.map((option, index) => ({
      ...option,
      display_order: index,
      name: optionNameIsDefault(option.name)
        ? `Option ${index + 1}`
        : option.name,
    }));

    updateSection({ options: newOptions });
    setIsDragging(false);
  };

  const onClickAddOption = () => {
    const sectionCopy = { ...section };
    const newOptions = sectionCopy.options;
    const emptyOption = makeEmptyOption(newOptions.length);
    newOptions.push(emptyOption);
    updateSection({ options: newOptions });
  };

  return (
    <Paper className={classes.section}>
      <Snackbar
        open={undoVisible}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message="Option deleted!"
        action={
          <>
            <Button color="secondary" onClick={onClickUndoDelete}>
              UNDO
            </Button>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={() => setUndoVisible(false)}
            >
              <CloseIcon />
            </IconButton>
          </>
        }
      />
      <Grid container>
        {section.quote_template_id !== null ? (
          <Grid md={8}>
            {isEditable ? (
              <Tooltip title="Click to edit section name" placement="left">
                <TextField
                  placeholder="Section Name"
                  type="text"
                  onChange={(e) => updateSection({ name: e.target.value })}
                  value={section.name}
                  InputProps={{
                    className: classes.sectionNameInput,
                    disableUnderline: true,
                  }}
                  label={null}
                  multiline
                />
              </Tooltip>
            ) : (
              <Typography variant="h2">
                {section.name || `Section ${sectionIndex + 1}`}
              </Typography>
            )}
          </Grid>
        ) : (
          <Grid md={8} />
        )}
        <Grid className={classes.headerContainerRight} md={4}>
          {isEditable && (
            <Tooltip title="Delete this section" placement="left">
              <IconButton aria-label="delete" onClick={deleteSection}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          {!!section.template && (
            <Tooltip
              title="This determines which fields are shown in each option"
              placement="left"
            >
              <Typography variant="p">
                Template: {section.template.name}
              </Typography>
            </Tooltip>
          )}
        </Grid>
      </Grid>
      {section.quote_template_id !== null && (
        <Grid md={8}>
          {isEditable ? (
            <Tooltip title="Click to edit section note" placement="left">
              <TextField
                placeholder="Add a note..."
                type="text"
                onChange={(e) => updateSection({ note: e.target.value })}
                value={section.note}
                InputProps={{
                  disableUnderline: true,
                }}
                label={null}
                multiline
                fullWidth
              />
            </Tooltip>
          ) : (
            <Typography variant="p">{section.note}</Typography>
          )}
        </Grid>
      )}
      {section.quote_template_id === null ? (
        <FormControl className={classes.formControl}>
          <InputLabel id="template-select-label">Select a Template</InputLabel>
          <Select
            id="template"
            labelId="template-select-label"
            value={section.quote_template_id}
            onChange={(e) => onSelectTemplate(e.target.value)}
          >
            {quoteTemplates.map((template) => {
              return (
                <MenuItem value={template.id}>
                  <Tooltip title={template.description} placement="right">
                    <Grid md={12}>
                      <Typography variant="p">{template.name}</Typography>
                    </Grid>
                  </Tooltip>
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText>
            This determines which fields are shown in each option
          </FormHelperText>
        </FormControl>
      ) : (
        <Grid>
          <DragDropContext
            onDragEnd={onDragOptionEnd}
            onBeforeDragStart={onDragOptionStart}
          >
            <Droppable droppableId="droppable" direction="horizontal">
              {(provided, droppableSnapshot) => (
                <div
                  ref={provided.innerRef}
                  className={classes.optionsContainer}
                  {...provided.droppableProps}
                >
                  {section.options.map((option, index) => (
                    <Draggable
                      key={option.id || option.key}
                      draggableId={`${option.id || option.key}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <QuoteOption
                            option={{ ...option, optionIndex: index }}
                            key={option.id}
                            updateOption={(key, value) =>
                              updateOption(index, key, value)
                            }
                            deleteOption={() => deleteOption(index)}
                            isDragging={snapshot.isDragging}
                            isInDropZone={droppableSnapshot.isDraggingOver}
                            isEditable={isEditable}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {isEditable && !isDragging && (
                    <AddOptionButton onClick={onClickAddOption} />
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Grid>
      )}
    </Paper>
  );
};

const AddOptionButton = ({ onClick }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.addOptionButton} onClick={onClick}>
      <AddIcon fontSize="large" />
      <Typography variant="p">Add New Option</Typography>
    </Paper>
  );
};

export default QuoteSection;
